import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type {
  ItemSyncStatusResponse,
  WorkIntegrationResponse,
} from '@/common/api/types/workIntegrations';
import { ItemSyncStatusView } from '@/common/api/types/workIntegrations';
import { useGetAllWorkIntegrationsById } from '@/features/settings/work-integrations/queries/useGetAllWorkIntegrations';
import { useGetWorkIntegrationIdsByItemIds } from '@/features/settings/work-integrations/queries/useGetWorkIntegrationItems';
import { useItemSyncStatusesQuery } from '@/features/settings/work-integrations/queries/useItemSyncStatusesQuery';

export type VisibleIntegrationValue = {
  integration: WorkIntegrationResponse | undefined;
  syncStatus: ItemSyncStatusResponse | undefined;
};

type VisibleIntegrationsContextType = {
  integrationIdsByItemId: Record<number, number>;
  syncStatusesByItemId: Record<number, ItemSyncStatusResponse>;
  integrationsById: Record<number, WorkIntegrationResponse>;
  resolvedIds: ReadonlySet<number>;
};

const EMPTY_INTEGRATION_IDS: Record<number, number> = {};
const EMPTY_SYNC_STATUSES: Record<number, ItemSyncStatusResponse> = {};
const EMPTY_INTEGRATIONS: Record<number, WorkIntegrationResponse> = {};
const EMPTY_VISIBLE_IDS: readonly number[] = [];
const EMPTY_RESOLVED_IDS: ReadonlySet<number> = new Set();

const VisibleIntegrationsContext = React.createContext<VisibleIntegrationsContextType | undefined>(
  undefined,
);

export const useVisibleIntegration = (
  id: number | undefined,
): VisibleIntegrationValue | undefined => {
  const context = React.useContext(VisibleIntegrationsContext);
  if (!context || id === undefined) return undefined;
  if (!context.resolvedIds.has(id)) return undefined;
  const integrationId = context.integrationIdsByItemId[id];
  const syncStatus = context.syncStatusesByItemId[id];
  return {
    integration: integrationId != null ? context.integrationsById[integrationId] : undefined,
    syncStatus,
  };
};

const IN_FLIGHT = (s?: ItemSyncStatusView) =>
  s === ItemSyncStatusView.PENDING || s === ItemSyncStatusView.IN_PROGRESS;

export const VisibleIntegrationsProvider = ({
  visibleItemIds,
  enabled = true,
  children,
}: {
  visibleItemIds: readonly number[];
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const ids = enabled ? visibleItemIds : EMPTY_VISIBLE_IDS;

  const integrationIdsQuery = useGetWorkIntegrationIdsByItemIds(ids as number[], {
    enabled: enabled && ids.length > 0,
    placeholderData: keepPreviousData,
  });
  const syncStatusesQuery = useItemSyncStatusesQuery(ids as number[], {
    placeholderData: keepPreviousData,
  });
  const allIntegrationsQuery = useGetAllWorkIntegrationsById({ enabled });

  const settled =
    integrationIdsQuery.isSuccess &&
    syncStatusesQuery.isSuccess &&
    allIntegrationsQuery.isSuccess &&
    !integrationIdsQuery.isFetching &&
    !syncStatusesQuery.isFetching;
  const [resolvedIds, setResolvedIds] = React.useState<ReadonlySet<number>>(EMPTY_RESOLVED_IDS);
  React.useEffect(() => {
    if (settled) setResolvedIds(new Set(ids));
  }, [settled, ids]);

  const queryClient = useQueryClient();
  const prevStatesRef = React.useRef<ReadonlyMap<number, ItemSyncStatusView>>(new Map());
  React.useEffect(() => {
    const curr = syncStatusesQuery.data ?? {};
    const visible = new Set(ids);
    let anyCompleted = false;
    for (const [id, prevState] of prevStatesRef.current) {
      if (!visible.has(id)) continue;
      if (IN_FLIGHT(prevState) && !IN_FLIGHT(curr[id]?.state)) anyCompleted = true;
    }
    if (anyCompleted) {
      queryClient.invalidateQueries({ queryKey: ['workIntegrations', 'items'] });
    }
    const next = new Map<number, ItemSyncStatusView>();
    for (const [idStr, s] of Object.entries(curr)) next.set(Number(idStr), s.state);
    prevStatesRef.current = next;
  }, [syncStatusesQuery.data, ids, queryClient]);

  const value = React.useMemo<VisibleIntegrationsContextType>(
    () => ({
      integrationIdsByItemId: integrationIdsQuery.data ?? EMPTY_INTEGRATION_IDS,
      syncStatusesByItemId: syncStatusesQuery.data ?? EMPTY_SYNC_STATUSES,
      integrationsById: allIntegrationsQuery.data ?? EMPTY_INTEGRATIONS,
      resolvedIds,
    }),
    [integrationIdsQuery.data, syncStatusesQuery.data, allIntegrationsQuery.data, resolvedIds],
  );

  return (
    <VisibleIntegrationsContext.Provider value={value}>
      {children}
    </VisibleIntegrationsContext.Provider>
  );
};
